import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listAgentSeats,
  deleteAgentSeat,
  getAgentSeatsAsterisk,
  seatAsteriskState,
  isSafeWebExtensionName,
  UNSAFE_WEB_EXTENSION_NAME_MESSAGE,
  // Shape-generic over FastAPI's {detail}/{error} bodies — not seat-specific
  // despite the name. Reused for agent-login errors too (the plan's own
  // Task 2.2 spec calls for reusing it rather than duplicating the same
  // "unwrap a 409's detail" logic a second time).
  agentSeatErrorMessage,
  agentSeatStatus,
  type AgentSeat,
  type AgentSeatAsterisk,
  type SeatAsteriskState,
} from "../api/extensions";
import {
  listAgentLogins,
  createAgentLogin,
  patchAgentLogin,
  resetAgentPassword,
  forceLogoutAgentLogin,
  provisionAgent,
  type AgentLogin,
  type AgentProvisioned,
} from "../api/agentLogins";
import {
  listQueueCampaigns,
  listQueueMemberships,
  setAgentQueueMemberships,
  type QueueCampaign,
  type QueueMembership,
} from "../api/queues";
import { QueueCampaignPicker, QUEUE_KEYS } from "../components/QueueAssignmentMatrix";

// The provisioning timer runs every ~2 minutes
// (ops/webrtc-agent-autoprovision), so a brand-new seat is not registerable the
// instant it appears in this list. Saying so up front is the difference between
// "it's still setting up" and "this product is broken".
const PROVISION_HINT = "Ready for sign-in within about 2 minutes.";

// The phone system's own answer, polled slowly (it is an AMI round-trip, not a
// table read). Every seat carries one of these so a seat that exists in the
// database and nowhere else — the failure that cost a rep a whole shift — is
// visibly different from a working one.
const ASTERISK_POLL_MS = 30_000;

const ASTERISK_BADGE: Record<SeatAsteriskState, { label: string; tone: string; title: string }> = {
  registered: {
    label: "phone connected",
    tone: "bg-green-100 text-green-800",
    title: "The phone system has this extension and a softphone is signed into it right now.",
  },
  provisioned: {
    label: "ready",
    tone: "bg-green-50 text-green-700",
    title: "The phone system has this extension. No softphone is connected at the moment.",
  },
  pending: {
    label: "setting up…",
    tone: "bg-amber-100 text-amber-800",
    title: "Just created. The phone system picks up new extensions within about 2 minutes.",
  },
  missing: {
    label: "not on phone system",
    tone: "bg-red-100 text-red-800",
    title:
      "This extension exists here but the phone system does not have it, so nobody can sign " +
      "in on it and no call can reach it. Contact support — do not put an agent on this seat.",
  },
  unknown: {
    label: "unknown",
    tone: "bg-gray-200 text-gray-600",
    title: "Could not ask the phone system just now. This says nothing about the seat itself.",
  },
};

function AsteriskBadge({ seat, view }: { seat: AgentSeat; view: AgentSeatAsterisk | undefined }) {
  const state = seatAsteriskState(seat, view);
  const b = ASTERISK_BADGE[state];
  return (
    <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${b.tone}`} title={b.title}>
      {b.label}
    </span>
  );
}

/** Queue chips for one seat, red when Asterisk will not ring the member. */
function QueueChips({
  memberships,
  onEdit,
}: {
  memberships: QueueMembership[];
  onEdit: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {memberships.length === 0 && <span className="text-xs text-gray-400">no queues</span>}
      {memberships.map((m) => (
        <span
          key={m.queue_member_id}
          title={m.valid ? undefined : `Assigned, but the phone system will skip this agent: ${m.invalid_reason}`}
          className={`px-1.5 py-0.5 rounded text-[11px] ${
            m.valid ? "bg-gray-100 text-gray-700" : "bg-red-100 text-red-800"
          }`}
        >
          {m.campaign_name}
          {!m.valid && " · not usable"}
        </span>
      ))}
      <button onClick={onEdit} className="text-brand hover:underline text-xs ml-1">
        Edit
      </button>
    </div>
  );
}

function StatusBadge({ seat }: { seat: AgentSeat }) {
  const status = agentSeatStatus(seat);
  if (!status) {
    return <span className="text-gray-400 text-xs">offline</span>;
  }
  const tone =
    status === "on a call"
      ? "bg-brand/10 text-brand-dark"
      : status === "on break"
        ? "bg-amber-100 text-amber-800"
        : "bg-green-100 text-green-800";
  return <span className={`px-2 py-0.5 rounded text-xs ${tone}`}>{status}</span>;
}

/** Username + status + the per-login actions. Shared by the per-seat row and
 * the "sign-ins not on a current seat" table, so the two never drift apart.
 *
 * `extensionLabel` is only passed by the orphaned-logins table, to show which
 * seat (if any) a login used to be bound to — otherwise "never bound" and
 * "used to be seat 2000" both render as an unlabeled row. */
function LoginControls({
  login,
  busy,
  onResetPassword,
  onToggleActive,
  onForceLogout,
  extensionLabel,
}: {
  login: AgentLogin;
  busy: boolean;
  onResetPassword: (login: AgentLogin) => void;
  onToggleActive: (login: AgentLogin) => void;
  onForceLogout: (login: AgentLogin) => void;
  extensionLabel?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="font-medium">{login.username}</span>
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] ${
            login.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
          }`}
        >
          {login.is_active ? "active" : "deactivated"}
        </span>
      </div>
      {extensionLabel && <p className="text-[11px] text-gray-400">{extensionLabel}</p>}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-0.5">
        <button
          disabled={busy}
          onClick={() => onResetPassword(login)}
          className="text-brand hover:underline disabled:opacity-50"
        >
          Reset password
        </button>
        <button
          disabled={busy}
          onClick={() => onToggleActive(login)}
          className="text-gray-600 hover:underline disabled:opacity-50"
        >
          {login.is_active ? "Deactivate" : "Reactivate"}
        </button>
        <button
          disabled={busy || !login.is_active}
          title={
            !login.is_active
              ? "Deactivating already ended their session — nothing left to sign out of."
              : undefined
          }
          onClick={() => onForceLogout(login)}
          className="text-red-600 hover:underline disabled:opacity-40"
        >
          Force sign-out
        </button>
      </div>
      {!login.is_active && (
        // A hover-only title is easy to miss on a disabled button — say the
        // same thing inline. Deactivating now performs the full stand-down
        // itself (presence + heartbeat key cleared immediately), so there is
        // deliberately nothing left for Force sign-out to do here.
        <p className="text-[11px] text-gray-400 mt-0.5">
          Deactivating already ended their session.
        </p>
      )}
    </div>
  );
}

/** One form, one request: the seat, its sign-in, and its queues. The backend
 * writes all three in a single transaction, so a refused sign-in name (say)
 * leaves no orphan seat behind — which is what made by-hand batches fragile. */
function AddAgentModal({
  campaigns,
  onClose,
  onCreated,
}: {
  campaigns: QueueCampaign[];
  onClose: () => void;
  onCreated: (agent: AgentProvisioned) => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [extension, setExtension] = useState("");
  const [username, setUsername] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [queues, setQueues] = useState<Set<number>>(new Set());

  const extensionBad = extension.trim() !== "" && !isSafeWebExtensionName(extension.trim());

  const create = useMutation({
    mutationFn: () =>
      provisionAgent({
        display_name: displayName.trim(),
        username: username.trim(),
        password,
        extension_name: extension.trim() || undefined,
        campaign_ids: [...queues],
      }),
    onSuccess: onCreated,
  });

  const canSubmit =
    displayName.trim() !== "" && username.trim() !== "" && password.length >= 8 && !extensionBad;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="text-lg font-bold">Add an agent</h2>
          <p className="text-sm text-gray-500 mt-1">
            Creates their extension, their sign-in and their queue assignments together.{" "}
            {PROVISION_HINT}
          </p>
        </div>
        <div>
          <label htmlFor="agent-name" className="block text-xs text-gray-500 mb-1">
            Agent name
          </label>
          <input
            id="agent-name"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="e.g. Claudia Castellar"
            value={displayName}
            maxLength={128}
            onChange={(e) => {
              setDisplayName(e.target.value);
              if (!usernameTouched) setUsername(e.target.value);
            }}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="agent-extension" className="block text-xs text-gray-500 mb-1">
              Extension number <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="agent-extension"
              className={`w-full border rounded px-3 py-2 text-sm ${extensionBad ? "border-red-400" : "border-gray-300"}`}
              placeholder="next free"
              value={extension}
              maxLength={64}
              onChange={(e) => setExtension(e.target.value)}
            />
            {extensionBad && (
              <p className="text-[11px] text-red-600 mt-1">{UNSAFE_WEB_EXTENSION_NAME_MESSAGE}</p>
            )}
          </div>
          <div>
            <label htmlFor="agent-username" className="block text-xs text-gray-500 mb-1">
              Sign-in name
            </label>
            <input
              id="agent-username"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={username}
              maxLength={128}
              onChange={(e) => {
                setUsernameTouched(true);
                setUsername(e.target.value);
              }}
            />
          </div>
        </div>
        <div>
          <label htmlFor="agent-password" className="block text-xs text-gray-500 mb-1">
            Password
          </label>
          <input
            id="agent-password"
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={password}
            maxLength={128}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <span className="block text-xs text-gray-500 mb-1">Takes calls from</span>
          <QueueCampaignPicker campaigns={campaigns} value={queues} onChange={setQueues} />
        </div>
        {create.isError && (
          <p className="text-sm text-red-600">{agentSeatErrorMessage(create.error)}</p>
        )}
        <div className="flex gap-2 pt-1">
          <button
            disabled={create.isPending || !canSubmit}
            onClick={() => create.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create agent"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Change which queues one existing seat takes calls from. Saves the full
 * wanted set in one call, so "move from inbound to outbound" is one save. */
function AgentQueuesModal({
  seat,
  campaigns,
  memberships,
  onClose,
  onSaved,
}: {
  seat: AgentSeat;
  campaigns: QueueCampaign[];
  memberships: QueueMembership[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [queues, setQueues] = useState<Set<number>>(
    () => new Set(memberships.map((m) => m.campaign_id)),
  );
  const invalid = useMemo(
    () => new Map(memberships.filter((m) => !m.valid).map((m) => [m.campaign_id, m.invalid_reason ?? ""])),
    [memberships],
  );
  const save = useMutation({
    mutationFn: () => setAgentQueueMemberships(seat.extension_id, [...queues]),
    onSuccess: onSaved,
  });
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="text-lg font-bold">Queues for {seat.title || seat.name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            Tick every queue this agent should take calls from. Takes effect on the next call.
          </p>
        </div>
        <QueueCampaignPicker
          campaigns={campaigns}
          value={queues}
          onChange={setQueues}
          disabled={save.isPending}
          invalidByCampaign={invalid}
        />
        {invalid.size > 0 && (
          <p className="text-xs text-red-700">
            Red queues are assigned but the phone system will skip this agent there:{" "}
            {[...invalid.values()].join("; ")}.
          </p>
        )}
        {save.isError && <p className="text-sm text-red-600">{agentSeatErrorMessage(save.error)}</p>}
        <div className="flex gap-2 pt-1">
          <button
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save queues"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Create a sign-in (username + password) for one seat. A seat is the phone
 * line; this is the credential a person types in to use it — so the modal is
 * always opened from a specific row, and `extension_id` rides along
 * unconditionally rather than being picked from a list. */
function CreateLoginModal({
  seat,
  onClose,
  onCreated,
}: {
  seat: AgentSeat;
  onClose: () => void;
  onCreated: (login: AgentLogin) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createAgentLogin({
        username: username.trim(),
        password,
        display_name: displayName.trim() ? displayName.trim() : undefined,
        extension_id: seat.extension_id,
      }),
    onSuccess: onCreated,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="text-lg font-bold">Create a sign-in for {seat.name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            This agent will sign in with their own name and password — never hand out your
            own login.
          </p>
        </div>
        <div>
          <label htmlFor="login-username" className="block text-xs text-gray-500 mb-1">
            Sign-in name
          </label>
          <input
            id="login-username"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={username}
            maxLength={128}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="login-password" className="block text-xs text-gray-500 mb-1">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={password}
            maxLength={128}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="login-display-name" className="block text-xs text-gray-500 mb-1">
            Display name <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="login-display-name"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={displayName}
            maxLength={128}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        {create.isError && (
          <p className="text-sm text-red-600">{agentSeatErrorMessage(create.error)}</p>
        )}
        <div className="flex gap-2 pt-1">
          <button
            disabled={create.isPending || !username.trim() || password.length < 8}
            onClick={() => create.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** Set a new password for an existing sign-in. The operator types the new
 * password in (the API has no "generate one for me" mode — `AgentLoginPassword`
 * takes a caller-supplied password, same as create), and this screen never
 * echoes it back afterwards: the operator already knows what they just typed,
 * so redisplaying it only adds a shoulder-surfing/screenshot risk with no
 * offsetting benefit. */
function ResetPasswordModal({
  login,
  onClose,
  onReset,
}: {
  login: AgentLogin;
  onClose: () => void;
  onReset: () => void;
}) {
  const [password, setPassword] = useState("");

  const reset = useMutation({
    mutationFn: () => resetAgentPassword(login.agent_user_id, password),
    onSuccess: onReset,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="text-lg font-bold">Reset password — {login.username}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {login.username} is signed out everywhere the moment you save this — they'll
            need the new password to sign back in. We won't show it on screen again, so
            share it with them directly.
          </p>
        </div>
        <div>
          <label htmlFor="reset-password" className="block text-xs text-gray-500 mb-1">
            New password
          </label>
          <input
            id="reset-password"
            type="password"
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={password}
            maxLength={128}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[11px] text-gray-400 mt-1">At least 8 characters.</p>
        </div>
        {reset.isError && (
          <p className="text-sm text-red-600">{agentSeatErrorMessage(reset.error)}</p>
        )}
        <div className="flex gap-2 pt-1">
          <button
            disabled={reset.isPending || password.length < 8}
            onClick={() => reset.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {reset.isPending ? "Saving…" : "Set password"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function MyAgentsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [justCreated, setJustCreated] = useState<AgentProvisioned | null>(null);
  const [createLoginFor, setCreateLoginFor] = useState<AgentSeat | null>(null);
  const [queuesFor, setQueuesFor] = useState<AgentSeat | null>(null);
  const [resetPasswordFor, setResetPasswordFor] = useState<AgentLogin | null>(null);
  const [passwordJustSetFor, setPasswordJustSetFor] = useState<string | null>(null);
  const [loginActionError, setLoginActionError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["agent-seats"],
    queryFn: listAgentSeats,
    // Presence comes back with the list, so a slow poll keeps the status column
    // honest without a second endpoint.
    refetchInterval: 10_000,
  });

  const {
    data: loginData,
    isLoading: loginsLoading,
    isError: loginsIsError,
    error: loginsError,
  } = useQuery({
    queryKey: ["agent-logins"],
    queryFn: listAgentLogins,
  });

  // Ground truth from the PBX. Slow poll: it is an AMI round-trip.
  const { data: asterisk } = useQuery({
    queryKey: ["agent-seats", "asterisk"],
    queryFn: getAgentSeatsAsterisk,
    refetchInterval: ASTERISK_POLL_MS,
  });

  const { data: campaigns } = useQuery({
    queryKey: QUEUE_KEYS.campaigns,
    queryFn: listQueueCampaigns,
  });
  const { data: memberships } = useQuery({
    queryKey: QUEUE_KEYS.memberships,
    queryFn: listQueueMemberships,
  });
  const membershipsBySeat = useMemo(() => {
    const m = new Map<number, QueueMembership[]>();
    for (const r of memberships ?? []) {
      const list = m.get(r.extension_id) ?? [];
      list.push(r);
      m.set(r.extension_id, list);
    }
    return m;
  }, [memberships]);
  const invalidateQueues = () => {
    qc.invalidateQueries({ queryKey: QUEUE_KEYS.memberships });
    qc.invalidateQueries({ queryKey: QUEUE_KEYS.campaigns });
  };

  const remove = useMutation({
    mutationFn: (id: number) => deleteAgentSeat(id),
    onSuccess: (_res, id) => {
      if (justCreated?.extension_id === id) setJustCreated(null);
      qc.invalidateQueries({ queryKey: ["agent-seats"] });
      invalidateQueues();
    },
  });

  const invalidateLogins = () => qc.invalidateQueries({ queryKey: ["agent-logins"] });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      patchAgentLogin(id, { is_active: active }),
    onSuccess: () => {
      setLoginActionError(null);
      invalidateLogins();
    },
    onError: (e) => setLoginActionError(agentSeatErrorMessage(e)),
  });

  const forceLogout = useMutation({
    mutationFn: (id: number) => forceLogoutAgentLogin(id),
    onSuccess: () => {
      setLoginActionError(null);
      invalidateLogins();
    },
    onError: (e) => setLoginActionError(agentSeatErrorMessage(e)),
  });

  const seats = data?.seats ?? [];
  const atCap = data ? data.used >= data.cap : false;

  const logins = loginData?.agents ?? [];
  const loginAtCap = loginData ? loginData.used >= loginData.cap : false;

  // Keyed by seat (extension_id), for the per-seat "Sign-in" column.
  const loginBySeat = useMemo(() => {
    const m = new Map<number, AgentLogin>();
    for (const l of logins) {
      if (l.extension_id !== null) m.set(l.extension_id, l);
    }
    return m;
  }, [logins]);

  // Logins whose seat isn't in the CURRENT live seat list — either never bound
  // (extension_id null) or bound to a seat that has since been removed. Extension
  // deletes are soft (is_deleted, not a real DELETE), so the login's binding
  // survives the seat's removal and would otherwise be invisible here while
  // still occupying a slot against the cap below.
  const liveSeatIds = useMemo(() => new Set(seats.map((s) => s.extension_id)), [seats]);
  const orphanedLogins = useMemo(
    () => logins.filter((l) => l.extension_id === null || !liveSeatIds.has(l.extension_id)),
    [logins, liveSeatIds],
  );

  const busyLoginId = setActive.isPending
    ? (setActive.variables?.id ?? null)
    : forceLogout.isPending
      ? (forceLogout.variables ?? null)
      : null;

  function handleToggleActive(login: AgentLogin) {
    if (
      login.is_active &&
      !confirm(
        `Deactivate ${login.username}? They won't be able to sign in until you reactivate ` +
          `them. Their sign-in name stays reserved either way — it can't be reused.`,
      )
    ) {
      return;
    }
    setActive.mutate({ id: login.agent_user_id, active: !login.is_active });
  }

  function handleForceLogout(login: AgentLogin) {
    // Resolved Decisions #5: this is a real stand-down, not a soft "sign out" —
    // say so before firing it.
    if (
      !confirm(
        `Force ${login.username} to sign out right now? This ends their session ` +
          `immediately, takes them off their seat in the dialer, and clears their ` +
          `online status. They can sign back in straight away — this does not ` +
          `deactivate their sign-in.`,
      )
    ) {
      return;
    }
    forceLogout.mutate(login.agent_user_id);
  }

  return (
    <div className="max-w-5xl">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">My agents</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            A seat is the phone line an agent takes calls on; a sign-in is the personal
            credential a person uses to use one. Create a seat below, then give each agent
            their own sign-in for the{" "}
            <Link to="/agent" className="text-brand hover:underline">
              Agent Panel
            </Link>{" "}
            — never your own login.
          </p>
        </div>
        <div className="text-right">
          <button
            onClick={() => setShowNew(true)}
            disabled={atCap}
            title={atCap ? `You have reached your limit of ${data?.cap} agents` : undefined}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            Add agent
          </button>
          {data && (
            <p className="text-xs text-gray-400 mt-1">
              {data.used} of {data.cap} seats used
            </p>
          )}
        </div>
      </div>

      {asterisk && !asterisk.reachable && (
        <p className="mb-3 text-xs text-gray-500">
          Could not reach the phone system to confirm which seats it has loaded — the
          “unknown” badges below say nothing about the seats themselves.
        </p>
      )}
      {asterisk?.reachable &&
        seats.some((seat) => seatAsteriskState(seat, asterisk) === "missing") && (
          <div className="mb-3 bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm">
            {seats.filter((seat) => seatAsteriskState(seat, asterisk) === "missing").length} seat(s)
            exist here but not on the phone system. An agent signed in on one of them will
            never receive a call. Contact support before putting anyone on them.
          </div>
        )}

      {loginData && (
        <p className="text-xs text-gray-400 mb-3">
          Sign-ins: {loginData.used} of {loginData.cap} used{loginAtCap ? " — limit reached" : ""}.
          Deactivated sign-ins still count toward this, since a sign-in name is never reused
          once created — contact support to raise the limit.
        </p>
      )}
      {loginsIsError && (
        <p className="mb-3 text-sm text-red-600">{agentSeatErrorMessage(loginsError)}</p>
      )}

      {justCreated && (
        <div className="mb-4 bg-white rounded-lg shadow border-l-4 border-brand p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">New agent extension</p>
            {/* The number is the one thing the operator has to write down and
                pass to their agent, so it gets the largest type on the page. */}
            <p className="text-3xl font-bold tracking-wide tabular-nums">{justCreated.extension_name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {justCreated.title} · signs in as{" "}
              <span className="font-medium">{justCreated.username}</span>
              {justCreated.campaign_ids.length > 0 &&
                ` · ${justCreated.campaign_ids.length} queue${justCreated.campaign_ids.length === 1 ? "" : "s"}`}
              {" · "}
              {PROVISION_HINT} The “Phone system” column turns green when it is.
            </p>
          </div>
          <button
            onClick={() => setJustCreated(null)}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {passwordJustSetFor && (
        <div className="mb-4 bg-white rounded-lg shadow border-l-4 border-brand p-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm">
              Password updated for <span className="font-semibold">{passwordJustSetFor}</span>.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Share the new password with them directly — this screen won't show it again.
            </p>
          </div>
          <button
            onClick={() => setPasswordJustSetFor(null)}
            aria-label="Dismiss"
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {remove.isError && (
        <p className="mb-3 text-sm text-red-600">{agentSeatErrorMessage(remove.error)}</p>
      )}
      {loginActionError && <p className="mb-3 text-sm text-red-600">{loginActionError}</p>}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{agentSeatErrorMessage(error)}</p>
      ) : seats.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="font-medium">No agents yet</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Add one for each person who will take transferred calls in the browser.
          </p>
          <button
            onClick={() => setShowNew(true)}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
          >
            Add agent
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Extension</th>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Sign-in</th>
                <th className="px-4 py-2">Queues</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Phone system</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {seats.map((seat) => {
                const login = loginBySeat.get(seat.extension_id) ?? null;
                return (
                  <tr key={seat.extension_id}>
                    <td className="px-4 py-2 font-semibold tabular-nums">{seat.name}</td>
                    <td className="px-4 py-2 text-gray-600">{seat.title || "—"}</td>
                    <td className="px-4 py-2">
                      {loginsLoading ? (
                        <span className="text-gray-400 text-xs">Loading…</span>
                      ) : login ? (
                        <LoginControls
                          login={login}
                          busy={busyLoginId === login.agent_user_id}
                          onResetPassword={setResetPasswordFor}
                          onToggleActive={handleToggleActive}
                          onForceLogout={handleForceLogout}
                        />
                      ) : (
                        <button
                          onClick={() => setCreateLoginFor(seat)}
                          disabled={loginAtCap}
                          title={
                            loginAtCap
                              ? `You've reached your limit of ${loginData?.cap} sign-ins.`
                              : undefined
                          }
                          className="text-brand hover:underline text-sm disabled:opacity-50 disabled:no-underline disabled:text-gray-400"
                        >
                          Create login
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <QueueChips
                        memberships={membershipsBySeat.get(seat.extension_id) ?? []}
                        onEdit={() => setQueuesFor(seat)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge seat={seat} />
                    </td>
                    <td className="px-4 py-2">
                      <AsteriskBadge seat={seat} view={asterisk} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            confirm(
                              `Remove agent ${seat.name}? They will not be able to sign in ` +
                                `or take calls afterwards.`,
                            )
                          ) {
                            remove.mutate(seat.extension_id);
                          }
                        }}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {orphanedLogins.length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            Sign-ins not on a current seat
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {orphanedLogins.map((login) => (
                <tr key={login.agent_user_id}>
                  <td className="px-4 py-2">
                    <LoginControls
                      login={login}
                      busy={busyLoginId === login.agent_user_id}
                      onResetPassword={setResetPasswordFor}
                      onToggleActive={handleToggleActive}
                      onForceLogout={handleForceLogout}
                      extensionLabel={
                        login.extension_id === null
                          ? "Never bound to a seat"
                          : `Was bound to extension #${login.extension_id}`
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <AddAgentModal
          campaigns={campaigns ?? []}
          onClose={() => setShowNew(false)}
          onCreated={(agent) => {
            setShowNew(false);
            setJustCreated(agent);
            qc.invalidateQueries({ queryKey: ["agent-seats"] });
            invalidateLogins();
            invalidateQueues();
          }}
        />
      )}

      {queuesFor && (
        <AgentQueuesModal
          seat={queuesFor}
          campaigns={campaigns ?? []}
          memberships={membershipsBySeat.get(queuesFor.extension_id) ?? []}
          onClose={() => setQueuesFor(null)}
          onSaved={() => {
            setQueuesFor(null);
            invalidateQueues();
          }}
        />
      )}

      {createLoginFor && (
        <CreateLoginModal
          seat={createLoginFor}
          onClose={() => setCreateLoginFor(null)}
          onCreated={() => {
            setCreateLoginFor(null);
            invalidateLogins();
          }}
        />
      )}

      {resetPasswordFor && (
        <ResetPasswordModal
          login={resetPasswordFor}
          onClose={() => setResetPasswordFor(null)}
          onReset={() => {
            setPasswordJustSetFor(resetPasswordFor.username);
            setResetPasswordFor(null);
            invalidateLogins();
          }}
        />
      )}
    </div>
  );
}
